// ---------------------------------------------------------------------------
// The admin sign-in, on the design system (R13)
//
// /admin/login, in German: NDI's operators sign in to the admin area with one
// password, POST /admin/login {password} (server/routes/admin.ts). The admin
// area keeps its own session apart from the app's (REDESIGN-PLAN 4): the
// server sets an HttpOnly admin_token cookie, and a success is a full page
// load of /admin, which checks it itself.
//
// What it does is what it did (tests/dom/admin-login.dom.ts pinned it first):
//   - an empty password is refused at the field, "Bitte Passwort eingeben.",
//     and nothing is sent;
//   - the server's refusal in its own words at the field; without words, or
//     without an answer: "Login fehlgeschlagen.";
//   - the error marks the field invalid and describes it (TextField), and is
//     announced: the error the field shows is a role="alert" span, as the
//     page's own message was;
//   - Enter submits: the field and the button are one form;
//   - the button is busy while it runs, keeps its label and takes no second
//     click; after a refusal the field takes the focus the busy button lost
//     (Chrome gives it back to the button once it is enabled again).
// New: AuthLayout with lang="de", the legal texts under the card, and the
// field autofilled as the current password.
//
// A LitElement with light DOM, so the page holds its own state without
// functional-lit; AdminLogin() is the template the router's map renders.
// ---------------------------------------------------------------------------

import { html, LitElement } from 'lit';

import { button } from '../design/button.js';
import { textField } from '../design/field.js';
import { standalonePage } from './standalone-page.js';

const EMPTY = 'Bitte Passwort eingeben.';
const FAILED = 'Login fehlgeschlagen.';

export class SdrAdminLoginPage extends LitElement {
  static override properties = { password: { state: true }, error: { state: true }, loading: { state: true } };

  declare password: string;
  declare error: string;
  declare loading: boolean;

  constructor() {
    super();
    this.password = '';
    this.error = '';
    this.loading = false;
  }

  override createRenderRoot() {
    return this;
  }

  private async submit(e: Event) {
    e.preventDefault();
    if (this.loading) return;
    this.error = '';
    if (!this.password) {
      this.error = EMPTY;
      return;
    }
    this.loading = true;
    try {
      const resp = await fetch('/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: this.password }),
      });
      const data = (await resp.json()) as { ok?: unknown; error?: unknown } | null;
      if (data?.ok) {
        window.location.href = '/admin';
        return;
      }
      this.error = typeof data?.error === 'string' && data.error.trim() ? data.error : FAILED;
    } catch {
      this.error = FAILED;
    } finally {
      this.loading = false;
    }
    this.keepFocus();
  }

  /**
   * A busy button is disabled, and a browser drops the focus of a disabled button: give it to the
   * field. Chrome hands it back to the button once it is enabled again, so the button counts as
   * dropped too (found in Chrome); a focus in the field, where Enter left it, stays.
   */
  private keepFocus() {
    void this.updateComplete.then(() => {
      const active = document.activeElement;
      const dropped =
        !active ||
        active === document.body ||
        !active.isConnected ||
        active === this.querySelector('button[type="submit"]');
      if (!dropped) return;
      this.querySelector<HTMLInputElement>('#admin-password')?.focus();
    });
  }

  override render() {
    return standalonePage(
      { lang: 'de', title: 'Admin Dashboard', description: 'Nur für NDI. Melden Sie sich mit dem Admin-Passwort an.' },
      html`<form novalidate @submit=${(e: Event) => void this.submit(e)}>
        ${textField({
          id: 'admin-password',
          label: 'Admin-Passwort',
          type: 'password',
          name: 'password',
          autocomplete: 'current-password',
          value: this.password,
          error: this.error ? html`<span role="alert">${this.error}</span>` : undefined,
          onInput: (e: InputEvent) => {
            this.password = (e.target as HTMLInputElement).value;
          },
        })}
        ${button({ type: 'submit', variant: 'primary', block: true, loading: this.loading }, 'Anmelden')}
      </form>`,
    );
  }
}

if (!customElements.get('sdr-admin-login-page')) customElements.define('sdr-admin-login-page', SdrAdminLoginPage);

/** The admin sign-in, as the router's map renders it. */
export const AdminLogin = () => html`<sdr-admin-login-page></sdr-admin-login-page>`;
